import React, { Component } from "react";

/* ============================================================================
 * DatePicker — ช่องเลือกวันที่ (dd/mm/yyyy) สำหรับหน้าเว็บขาย
 *
 * ★ ทำไมแยกออกมาจาก CustomComponent.js
 *   ตัวเดิมอยู่ในไฟล์รวม 1,500 บรรทัดของหลังบ้าน import แล้วลาก MUI / react-bootstrap /
 *   react-switch / css ของ admin ติดมาทั้งชุด ทั้งที่หน้าเว็บใช้แค่ช่องวันที่ช่องเดียว
 *
 * ★ ไม่ใช้ <input type="date"> เพราะรูปแบบที่แสดงขึ้นกับ locale ของเบราว์เซอร์
 *   (บางเครื่องเป็น mm/dd/yyyy) คนไทยกรอกผิดเดือน/วันสลับกันบ่อย
 *
 * props
 *   value / onChange  "YYYY-MM-DD" (ค.ศ.) หรือ "" — รูปเดียวกับที่ backend รับ
 *   type="th"         ชื่อเดือนภาษาไทย + ปี พ.ศ. (ค่าที่ส่งออกยังเป็น ค.ศ. เสมอ)
 *   min / max         "YYYY-MM-DD" — วันนอกช่วงกดไม่ได้ และพิมพ์เข้ามาก็ไม่รับ
 *   id                ผูกกับ <label htmlFor> ได้
 * ========================================================================== */

const MONTHS_TH = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS_TH = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];
const DAYS_EN = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const pad = (n) => String(n).padStart(2, "0");
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// "YYYY-MM-DD" → { y, m (0-11), d } · รูปแบบผิด = null
const parseIso = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
    if (!match) return null;
    return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) };
};

const today = () => {
    const now = new Date();
    return toIso(now.getFullYear(), now.getMonth(), now.getDate());
};

export default class DatePicker extends Component {
    state = { open: false, text: "", viewYear: 0, viewMonth: 0 };

    wrapRef = React.createRef();

    componentDidMount() {
        this.syncText();
        document.addEventListener("mousedown", this.onOutside);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value || prevProps.type !== this.props.type) this.syncText();
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.onOutside);
    }

    isThai = () => this.props.type === "th";

    format = (iso) => {
        const p = parseIso(iso);
        return p ? `${pad(p.d)}/${pad(p.m + 1)}/${p.y + (this.isThai() ? 543 : 0)}` : "";
    };

    syncText = () => this.setState({ text: this.format(this.props.value) });

    inRange = (iso) => {
        const { min, max } = this.props;
        return (!min || iso >= min) && (!max || iso <= max);
    };

    onOutside = (event) => {
        if (this.state.open && this.wrapRef.current && !this.wrapRef.current.contains(event.target)) {
            this.setState({ open: false });
        }
    };

    openCalendar = () => {
        if (this.props.disabled) return;
        // เปิดที่เดือนของค่าที่เลือกไว้ ไม่มีค่า = เดือนของวันที่เร็วที่สุดที่เลือกได้ (ไม่ใช่เดือนในอดีต)
        const base = parseIso(this.props.value) || parseIso(this.props.min && this.props.min > today() ? this.props.min : today());
        this.setState({ open: true, viewYear: base.y, viewMonth: base.m });
    };

    pick = (iso) => {
        if (iso && !this.inRange(iso)) return;
        this.props.onChange && this.props.onChange(iso);
        this.setState({ open: false, text: this.format(iso) });
    };

    // พิมพ์เอง : รับ d/m/yyyy หรือ ddmmyyyy · ปี > 2400 ถือเป็น พ.ศ.
    // ★ รูปแบบผิด / วันไม่มีจริง (31/02) / นอกช่วง min-max = กลับไปค่าเดิม ไม่ส่งค่าผิดออกไป
    commitText = () => {
        const raw = String(this.state.text || "").trim();
        if (!raw) {
            if (this.props.value) this.pick("");
            return;
        }
        const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw) || /^(\d{2})(\d{2})(\d{4})$/.exec(raw);
        if (match) {
            const d = Number(match[1]);
            const m = Number(match[2]) - 1;
            const y = Number(match[3]) > 2400 ? Number(match[3]) - 543 : Number(match[3]);
            const date = new Date(y, m, d);
            const iso = toIso(y, m, d);
            if (date.getFullYear() === y && date.getMonth() === m && date.getDate() === d && this.inRange(iso)) {
                if (iso !== this.props.value) this.pick(iso);
                else this.syncText();
                return;
            }
        }
        this.syncText();
    };

    moveMonth = (step) =>
        this.setState((s) => {
            const date = new Date(s.viewYear, s.viewMonth + step, 1);
            return { viewYear: date.getFullYear(), viewMonth: date.getMonth() };
        });

    onKeyDown = (event) => {
        if (event.key === "Escape" && this.state.open) {
            // ★ ปิดแค่ปฏิทิน ไม่ให้ Esc ทะลุไปปิดโมดัลที่ครอบอยู่
            event.stopPropagation();
            this.setState({ open: false });
        } else if (event.key === "Enter" && event.target.tagName === "INPUT") {
            event.preventDefault();
            this.commitText();
            this.setState({ open: false });
        }
    };

    renderCalendar() {
        const { viewYear, viewMonth } = this.state;
        const thai = this.isThai();
        const months = thai ? MONTHS_TH : MONTHS_EN;
        const firstDay = new Date(viewYear, viewMonth, 1).getDay();
        const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
        const selected = this.props.value;
        const now = today();

        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(<span key={`blank-${i}`} />);
        for (let d = 1; d <= daysInMonth; d++) {
            const iso = toIso(viewYear, viewMonth, d);
            const enabled = this.inRange(iso);
            cells.push(
                <button
                    key={iso}
                    type="button"
                    className={`hm-dp__day ${iso === selected ? "is-selected" : ""} ${iso === now ? "is-today" : ""}`}
                    disabled={!enabled}
                    aria-pressed={iso === selected}
                    aria-label={this.format(iso)}
                    onClick={() => this.pick(iso)}
                >
                    {d}
                </button>
            );
        }

        // เลื่อนไปเดือนก่อน min ไม่ได้ — ไม่มีวันให้เลือกอยู่แล้ว กดไปก็เจอปฏิทินเทาทั้งเดือน
        const { min, max } = this.props;
        const canPrev = !min || toIso(viewYear, viewMonth, 1) > min.slice(0, 8) + "01";
        const canNext = !max || toIso(viewYear, viewMonth, daysInMonth) < max;

        return (
            <div className="hm-dp__popup" role="dialog" aria-label="เลือกวันที่">
                <div className="hm-dp__head">
                    <button type="button" className="hm-dp__nav" aria-label="เดือนก่อนหน้า" disabled={!canPrev} onClick={() => this.moveMonth(-1)}>
                        ‹
                    </button>
                    <span className="hm-dp__title">
                        {months[viewMonth]} {viewYear + (thai ? 543 : 0)}
                    </span>
                    <button type="button" className="hm-dp__nav" aria-label="เดือนถัดไป" disabled={!canNext} onClick={() => this.moveMonth(1)}>
                        ›
                    </button>
                </div>
                <div className="hm-dp__grid">
                    {(thai ? DAYS_TH : DAYS_EN).map((d) => (
                        <span key={d} className="hm-dp__dow">
                            {d}
                        </span>
                    ))}
                    {cells}
                </div>
                <div className="hm-dp__foot">
                    {this.props.clearable !== false ? (
                        <button type="button" className="hm-dp__link" onClick={() => this.pick("")}>
                            ล้างค่า
                        </button>
                    ) : (
                        <span />
                    )}
                    <button type="button" className="hm-dp__link" disabled={!this.inRange(now)} onClick={() => this.pick(now)}>
                        วันนี้
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { id, placeholder, className, disabled } = this.props;
        return (
            <div className="hm-dp" ref={this.wrapRef} onKeyDown={this.onKeyDown}>
                <input
                    id={id}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={className}
                    placeholder={placeholder || (this.isThai() ? "วว/ดด/ปปปป (พ.ศ.)" : "dd/mm/yyyy")}
                    value={this.state.text}
                    disabled={disabled}
                    maxLength={10}
                    aria-haspopup="dialog"
                    onFocus={this.openCalendar}
                    onClick={this.openCalendar}
                    onChange={(e) => this.setState({ text: e.target.value })}
                    onBlur={this.commitText}
                />
                {this.state.open && this.renderCalendar()}
            </div>
        );
    }
}
